import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { CaseStudyCard, ContentListItem } from '@/lib/content/types'

import { useMemo } from 'react'
import { Layout } from '@/components/generals/layout'
import { absoluteUrl, Seo } from '@/components/generals/seo'
import { CtaBand, PageHero } from '@/components/site/primitives'
import { ArticleListing, industryFilters } from '@/components/site/article-listing'
import { isCaseStudyCard } from '@/components/site/article-meta'
import { listCaseStudies } from '@/lib/content/queries'
import { localePath } from '@/i18n/config'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

interface CaseStudiesPageProps {
    items: Array<CaseStudyCard>
}

const filterOf = (item: ContentListItem): string | null => isCaseStudyCard(item) ? item.industry : null

const CaseStudiesPage: FC<CaseStudiesPageProps> = ({ items }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale]
    const page = copy.caseStudies
    const filters = useMemo(() => industryFilters(items, locale), [items, locale])

    return (
        <Layout tone='dark'>
            <Seo
                title={page.seoTitle}
                description={page.seoDescription}
                path='/case-studies'
                breadcrumbs={[{ name: page.seoTitle, path: '/case-studies' }]}
                jsonLd={[{
                    '@context': 'https://schema.org',
                    '@type': 'CollectionPage',
                    name: page.seoTitle,
                    description: page.seoDescription,
                    url: absoluteUrl(localePath(locale, '/case-studies'))
                }]}
            />

            <PageHero tone='dark' eyebrow={page.eyebrow} title={page.title} text={page.text} />

            <ArticleListing
                items={items}
                filters={filters}
                filterOf={filterOf}
                allLabel={copy.listing.allIndustries}
                filterLabel={copy.listing.filterByIndustry}
                emptyTitle={page.emptyTitle}
                emptyText={page.emptyText}
            />

            <CtaBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<CaseStudiesPageProps> = async () => {
    try {
        const items = await listCaseStudies()
        return { props: { items }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load case studies', error)
        return { props: { items: [] }, revalidate: 60 }
    }
}

export default CaseStudiesPage
